import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router'
import { type TrainingProgram } from '#app/utils/cms.server.ts'
import { getLocalizedContent } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'
import { checkForTrainingMode } from '#app/utils/training.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { LanguageMenu } from './language-menu.tsx'
import { Button } from './ui/button.tsx'
import {
  Dialog,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx'
import { Icon } from './ui/icon.tsx'
import { UserMenu } from './user-menu.tsx'

const PATHS_FOR_ANONYMOUS_USER = {
  Home: '/',
  OurStory: '/our-story',
  Faq: '/faq',
  Terms: '/terms',
  Privacy: '/privacy',
}

function isCurrentLink(to: string, pathname: string) {
  if (to === '/') {
    return pathname === to
  }

  return pathname.includes(to)
}

function DesktopHeaderLink({
  to,
  text,
  authenticated = false,
}: {
  to: string
  text: string
  authenticated?: boolean
}) {
  const location = useLocation()
  const currentLink = isCurrentLink(to, location.pathname)

  let textClasses: string = ''
  if (currentLink) {
    textClasses += 'underline'
  } else if (!authenticated) {
    textClasses += 'text-mauve-11'
  }

  return (
    <Link
      className={`${textClasses} text-2 mr-4 text-nowrap hover:underline focus:underline focus:outline-hidden`}
      to={to}
    >
      {text}
    </Link>
  )
}

function DesktopHeaderDropdown({
  items,
  text,
}: {
  items: { to: string; text: string | undefined }[]
  text: string
}) {
  const location = useLocation()

  items.sort((a, b) => (a.text && b.text ? a.text.localeCompare(b.text) : 0))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`${text.toLowerCase()}-dropdown-trigger`}
          className="text-2 mr-4 flex cursor-pointer items-center border-none bg-transparent text-nowrap hover:underline focus:underline focus:outline-hidden"
        >
          {text}
          <ChevronDown className="ml-1 inline h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent sideOffset={8} align="center" className="p-0">
          {items.map(({ to, text }) => {
            const isCurrentProgram = isCurrentLink(to, location.pathname)
            return (
              <DropdownMenuItem key={to} asChild className="p-0">
                <Link
                  data-testid={text}
                  data-current={isCurrentProgram}
                  className={cn(
                    'flex w-full min-w-full items-center justify-between px-4 py-3',
                    isCurrentProgram && 'bg-cyan-4 text-cyan-11 font-medium',
                  )}
                  prefetch="intent"
                  to={to}
                >
                  <span>{text}</span>
                  {isCurrentProgram && (
                    <Icon name="check" className="text-cyan-11 ml-2 h-4 w-4" />
                  )}
                </Link>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  )
}

function DesktopHeader({
  enrolledTrainingPrograms,
  showFaqLink,
}: {
  enrolledTrainingPrograms?: TrainingProgram[]
  showFaqLink: boolean
}) {
  const location = useLocation()
  const user = useOptionalUser()
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  const trainingLinks =
    enrolledTrainingPrograms?.map(program => ({
      to: `/training/${program.slug.current}`,
      text: getLocalizedContent(program.title, locale),
    })) || []

  const curriculumLinks =
    enrolledTrainingPrograms?.map(program => ({
      to: `/lessons/${program.slug.current}`,
      text: getLocalizedContent(program.title, locale),
    })) || []

  return (
    <div
      className={cn(
        'container flex-nowrap items-center justify-between gap-8 py-4 lg:flex',
        !user && 'hidden',
        user && 'flex',
      )}
    >
      <div className="flex items-center">
        <Link to={'/'}>
          <span className="sr-only">SparkLing</span>
          <img src="/img/logo-horizontal.svg" alt="" width="162px" />
        </Link>
        <ul className="ml-6 flex">
          <li>
            <DesktopHeaderLink
              to={PATHS_FOR_ANONYMOUS_USER.OurStory}
              text={t('ourStory')}
            />
          </li>
          {showFaqLink && (
            <li>
              <DesktopHeaderLink
                to={PATHS_FOR_ANONYMOUS_USER.Faq}
                text={t('faq.pageTitle')}
              />
            </li>
          )}
        </ul>
      </div>
      <div className="flex items-center gap-4">
        {!user &&
          Object.values(PATHS_FOR_ANONYMOUS_USER).includes(
            location.pathname,
          ) && <DesktopHeaderLink to="/sign-in" text={t('signIn.button')} />}
        {user && (
          <>
            <ul className="ml-6 flex">
              <li className="flex items-center">
                <Icon name="dashboard" className="mr-[5px]" />
                <DesktopHeaderLink
                  to="/dashboard"
                  text={t('dashboard.title')}
                  authenticated
                />
                {trainingLinks.length > 0 && (
                  <span className="mr-4 text-black">|</span>
                )}
              </li>
              {trainingLinks.length > 0 && (
                <li className="flex items-center">
                  <Icon name="backpack" className="mr-[5px]" />
                  <DesktopHeaderDropdown
                    text={t('training.title')}
                    items={trainingLinks}
                  />
                  <span className="mr-4 text-black">|</span>
                </li>
              )}
              {curriculumLinks.length > 0 && (
                <li className="flex items-center">
                  <Icon name="blackboard" className="mr-[5px]" />
                  <DesktopHeaderDropdown
                    text={t('lessons.title')}
                    items={curriculumLinks}
                  />
                </li>
              )}
            </ul>
            <UserMenu />
          </>
        )}
        <LanguageMenu />
      </div>
    </div>
  )
}

function MobileLogo() {
  return (
    <img
      srcSet="/img/logo-horizontal.svg"
      src="/img/logo-horizontal.svg"
      alt=""
      width={104}
      height={18}
    />
  )
}

function MobileHeaderLink({
  to,
  text,
  onClick,
}: {
  to: string
  text: string
  onClick: () => void
}) {
  const location = useLocation()
  const currentLink = isCurrentLink(to, location.pathname)
  const textClasses = currentLink ? 'underline' : 'text-mauve-11'

  return (
    <Link
      className={`${textClasses} font-poppins text-4 font-medium underline-offset-4 hover:underline focus:underline focus:outline-hidden`}
      to={to}
      onClick={onClick}
    >
      {text}
    </Link>
  )
}

function MobileHeader({ showFaqLink }: { showFaqLink: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  const closeMenu = () => setOpen(false)

  const disableScrolling = () => {
    document.body.style.overflow = 'hidden'
  }

  const enableScrolling = () => {
    document.body.style.overflow = 'auto'
  }

  const gradientSize = '418px'
  const gradientStyles = {
    width: gradientSize,
    height: gradientSize,
  }

  return (
    <div className="flex items-center justify-between px-6 py-5 lg:hidden">
      <div>
        <Link to="/">
          <span className="sr-only">SparkLing</span>
          <MobileLogo />
        </Link>
      </div>
      <div className="flex items-center gap-3">
        <LanguageMenu />
        <Dialog open={open}>
          <DialogTrigger asChild>
            <Button
              className="ring-ring h-6 bg-transparent p-1 focus-within:ring-2 hover:bg-transparent hover:ring-3 focus:bg-transparent focus-visible:ring-2"
              aria-label={t('openMenu')}
              onClick={() => setOpen(!open)}
            >
              <Icon
                className="text-black-default h-5 w-5"
                name="hamburger-menu"
              />
            </Button>
          </DialogTrigger>
          <DialogPortal>
            <div className="bg-dialog-default data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 overflow-hidden outline-hidden" />
            <DialogPrimitive.Content
              className={cn(
                'bg-cyan-4 fixed top-0 left-0 z-50 grid h-screen w-screen overflow-hidden p-6',
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-200 sm:rounded-lg',
              )}
              onOpenAutoFocus={disableScrolling}
              onCloseAutoFocus={enableScrolling}
            >
              <div className="relative flex h-full flex-col">
                <div className="absolute top-[-32px] left-[-32px]">
                  <img src="/img/decorative/mobile-menu-shapes-1.svg" alt="" />
                </div>
                <div className="absolute top-[164px] right-[48px]">
                  <img src="/img/decorative/mobile-menu-star-1.svg" alt="" />
                </div>
                <div className="absolute bottom-[128px] left-[16px]">
                  <img src="/img/decorative/mobile-menu-star-2.svg" alt="" />
                </div>
                <div className="absolute right-[-32px] bottom-[-32px]">
                  <img src="/img/decorative/mobile-menu-shapes-2.svg" alt="" />
                </div>
                <div
                  className="bg-mobile-menu-1 absolute top-[-100px] right-[-194px]"
                  style={gradientStyles}
                />
                <div
                  className="bg-mobile-menu-2 absolute bottom-[-164px] left-[-168px]"
                  style={gradientStyles}
                />
                <div className="relative flex h-[22px] w-full items-center justify-between pt-1">
                  <div>
                    <DialogTitle className="sr-only">
                      {t('navigationMenu')}
                    </DialogTitle>
                    <MobileLogo />
                  </div>
                  <div>
                    <Button
                      className="ring-ring h-6 bg-transparent p-1 focus-within:ring-2 hover:bg-transparent hover:ring-3 focus:bg-transparent focus-visible:ring-2"
                      aria-label={t('closeMenu')}
                      onClick={closeMenu}
                    >
                      <Icon
                        className="text-black-default h-5 w-5"
                        name="cross-1"
                      />
                    </Button>
                  </div>
                </div>
                <ul className="relative flex w-full flex-1 flex-col items-center justify-center gap-6">
                  <li>
                    <MobileHeaderLink
                      to={PATHS_FOR_ANONYMOUS_USER.Home}
                      text={t('home')}
                      onClick={closeMenu}
                    />
                  </li>
                  <li>
                    <MobileHeaderLink
                      to={PATHS_FOR_ANONYMOUS_USER.OurStory}
                      text={t('ourStory')}
                      onClick={closeMenu}
                    />
                  </li>
                  {showFaqLink && (
                    <li>
                      <MobileHeaderLink
                        to={PATHS_FOR_ANONYMOUS_USER.Faq}
                        text={t('faq.pageTitle')}
                        onClick={closeMenu}
                      />
                    </li>
                  )}
                  <li>
                    <MobileHeaderLink
                      to="/sign-in"
                      text={t('signIn.button')}
                      onClick={closeMenu}
                    />
                  </li>
                </ul>
              </div>
            </DialogPrimitive.Content>
          </DialogPortal>
        </Dialog>
      </div>
    </div>
  )
}

function AppHeader({
  enrolledTrainingPrograms,
  showFaqLink,
}: {
  enrolledTrainingPrograms?: TrainingProgram[]
  showFaqLink: boolean
}) {
  const params = useParams()
  const user = useOptionalUser()

  const inTrainingMode = checkForTrainingMode(params)
  if (inTrainingMode) return null

  return (
    <header className="z-10">
      <nav aria-label="Main navigation">
        {!user && <MobileHeader showFaqLink={showFaqLink} />}
        <DesktopHeader
          enrolledTrainingPrograms={enrolledTrainingPrograms}
          showFaqLink={showFaqLink}
        />
      </nav>
    </header>
  )
}

export { AppHeader }
